import React, { useState } from 'react';
import { useGiftCardBalance } from './hooks/useGiftCardBalance';
import Input from '../common/input';
import InputPin from '../common/input-pin';

interface GiftCardProps {
    translate: (key: string) => string;
    giftCardPageUrl?: string;
}

interface GiftCardForm {
    giftcardNumber: string;
    pin: string;
}

const GiftCard = ({ giftCardPageUrl }: GiftCardProps) => {
    const { mutate } = useGiftCardBalance();
    const [formData, setFormData] = useState<GiftCardForm>({
        giftcardNumber: '',
        pin: '',
    });
    const [gcBalance, setGcBalance] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [errorResponse, setErrorResponse] = useState(null);
    const [fieldErrors, setFieldErrors] = useState<{ giftcardNumber?: string; pin?: string }>({});
    const [touchedFields, setTouchedFields] = useState<{ giftcardNumber: boolean; pin: boolean }>({
        giftcardNumber: false,
        pin: false,
    });

    const validateGiftcardNumber = (value: string): boolean => {
        const rawValue = value.replace(/\s/g, '');
        return /^[0-9]{19}$/.test(rawValue);
    };

    const validatePin = (value: string): boolean => {
        return /^[0-9]{4}$/.test(value);
    };

    const isGiftCardNumberValid = validateGiftcardNumber(formData.giftcardNumber);
    const isPinValid = validatePin(formData.pin);
    const isFormValid = isGiftCardNumberValid && isPinValid;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setSubmitted(false);
        setErrorResponse(null);

        if (name === 'giftcardNumber' && validateGiftcardNumber(value)) {
            setFieldErrors((prev) => ({ ...prev, giftcardNumber: undefined }));
        }
        if (name === 'pin' && validatePin(value)) {
            setFieldErrors((prev) => ({ ...prev, pin: undefined }));
        }
    };

    const checkBalance = (e: React.FormEvent) => {
        e.preventDefault();

        setSubmitted(true);

        if (!isFormValid) {
            setTouchedFields({ giftcardNumber: true, pin: true });
            return;
        }

        const { giftcardNumber, pin } = formData;
        const errors: { giftcardNumber?: string; pin?: string } = {};

        setTouchedFields({
            giftcardNumber: true,
            pin: true,
        });

        if (!validateGiftcardNumber(giftcardNumber)) {
            errors.giftcardNumber = 'Please enter a valid gift card number (19 numbers)';
        }
        if (!validatePin(pin)) {
            errors.pin = 'Please enter a valid PIN (4 numbers)';
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setGcBalance(null);
            return;
        }

        setFieldErrors({});

        mutate(
            {
                CardNumber: giftcardNumber.trim().replace(/\s/g, ''),
                PIN: pin.trim(),
            },
            {
                onSuccess: (response) => {
                    const balance = response.data;
                    setGcBalance(balance);
                    setErrorResponse(null);
                    resetForm();
                },
                onError: () => {
                    setErrorResponse('The system could not validate the Gift Card. Please try again.');
                    setGcBalance(null);
                },
            },
        );
    };

    const resetForm = () => {
        setFormData({
            giftcardNumber: '',
            pin: '',
        });
    };

    return (
        <div className="clearfix giftcard">
            <form id="gcForm" onSubmit={checkBalance} noValidate>
                <div>
                    <h1 className="text-center text-[20px] font-bold uppercase md:mb-0 md:text-left">
                        {'Check Gift Card Balance'}
                    </h1>

                    <div className="mb-[15px]">
                        <label
                            htmlFor="giftcardNumber"
                            className="mb-0 mt-2 inline-block max-w-full text-[16px] font-bold uppercase leading-[1.3]"
                        >
                            {'Gift Card Number'}
                        </label>
                        <Input
                            id="giftcardNumber"
                            name="giftcardNumber"
                            mask="giftcard"
                            error={touchedFields.giftcardNumber && fieldErrors.giftcardNumber}
                            value={formData.giftcardNumber}
                            onChange={handleInputChange}
                            required
                            type="text"
                            className="g-white block h-[36px] w-full border-0 border-b bg-none p-0 text-[14px] shadow-none focus:outline-none md:px-0 md:pb-[6px] md:pt-0"
                        />
                    </div>

                    <div className="mb-[15px]">
                        <label
                            htmlFor="pin"
                            className="mb-0 mt-2 inline-block max-w-full text-[16px] font-bold uppercase leading-[1.3]"
                        >
                            {'PIN #'}
                        </label>
                        <InputPin
                            id="pin"
                            name="pin"
                            mask="pin"
                            error={touchedFields.pin && fieldErrors.pin}
                            value={formData.pin}
                            onChange={handleInputChange}
                            required
                            type="text"
                            classProp={`g-white block h-[36px] w-full border-0 border-b bg-none p-0 text-[14px] shadow-none focus:outline-none md:w-1/2 md:px-0 md:pb-[6px] md:pt-0`}
                        />
                    </div>
                </div>
                <div>
                    <div className="mb-[15px]">
                        <button
                            type="submit"
                            disabled={!isFormValid || submitted}
                            className={`relative mt-[30px] block h-[42px] w-full rounded-none border border-black px-[10px] py-[10px] text-[14px] uppercase text-white opacity-100 md:font-bold ${
                                !isFormValid || submitted
                                    ? 'disabled:cursor-not-allowed disabled:bg-[#666] disabled:opacity-100 disabled:shadow-none'
                                    : 'bg-[black]'
                            }`}
                        >
                            {'Check Balance'}
                        </button>
                    </div>

                    {giftCardPageUrl && (
                        <div className="form-group">
                            <a
                                href={giftCardPageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-block border border-black px-3 py-2 font-bold"
                            >
                                {'Buy a Gift Card'}
                            </a>
                        </div>
                    )}

                    {gcBalance && (
                        <div className="mt-4 text-[14px] font-bold">
                            <strong>
                                {'Your Balance is:'} {gcBalance}
                            </strong>
                        </div>
                    )}

                    {errorResponse && (
                        <div className="-mt-[2vw] mb-[2vw] text-[14px] text-red-500 md:mb-2 md:mt-4">
                            {errorResponse}
                        </div>
                    )}
                </div>
            </form>
        </div>
    );
};

export default GiftCard;
